import React, { useRef, useState, useEffect } from "react";
import { CalendarClock, Check, Clock, RefreshCw, Calendar, ChevronDown } from "lucide-react";
import { BlackCross, WhiteCross } from "../../assets";
import { useCreateSchedulerJob, useUpdateSchedulerJob } from "../../api/queryHooks";

// ─── Schedule option definitions ──────────────────────────────────────────────

const INTERVAL_OPTIONS = [
  { key: "EVERY_15MIN", label: "15 min",  title: "Every 15 minutes" },
  { key: "EVERY_30MIN", label: "30 min",  title: "Every 30 minutes" },
  { key: "HOURLY",      label: "1 hour",  title: "Every hour"       },
];

// Full 24-hour range for Daily / Weekly pickers
const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = [0, 15, 30, 45];

const DAY_OPTIONS = [
  { key: "MON", label: "Monday" },
  { key: "TUE", label: "Tuesday" },
  { key: "WED", label: "Wednesday" },
  { key: "THU", label: "Thursday" },
  { key: "FRI", label: "Friday" },
  { key: "SAT", label: "Saturday" },
  { key: "SUN", label: "Sunday" },
];

const formatHour = (h) =>
  h === 0 ? "12 AM" : h < 12 ? `${h} AM` : h === 12 ? "12 PM" : `${h - 12} PM`;

const formatTime = (h, m) =>
  m === 0 ? formatHour(h) : `${formatHour(h).replace(" ", ":" + String(m).padStart(2, "0") + " ")}`;

// Top-level mode tabs
const MODE_TABS = [
  { id: "interval", label: "Interval", icon: RefreshCw },
  { id: "daily",    label: "Daily",    icon: Clock     },
  { id: "weekly",   label: "Weekly",   icon: Calendar  },
];

// ─── ScrollSelect ─────────────────────────────────────────────────────────────
// Generic Photon-styled scrollable dropdown
const ScrollSelect = ({ options, value, onChange, isDark, width = "w-28" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const current = options.find((o) => o.value === value);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-medium transition-colors ${
          isDark
            ? "bg-fill-dialog-border border-edge-muted-control text-ink-white hover:border-edge-selection-muted"
            : "bg-fill-white border-edge-border text-ink-ink hover:border-edge-icon-hover"
        }`}
      >
        {current?.label ?? value}
        <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className={`absolute z-50 mt-1 ${width} rounded-xl border shadow-lg overflow-y-auto max-h-44 show-scrollbar ${
          isDark ? "bg-fill-dialog-surface border-edge-dialog-border" : "bg-fill-white border-edge-gray-200"
        }`}>
          {options.map(({ value: v, label: l }) => {
            const active = v === value;
            return (
              <button
                key={v}
                type="button"
                onClick={() => { onChange(v); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 text-xs transition-colors ${
                  active
                    ? "photon-primary-action bg-button-primary text-button-on-primary font-semibold"
                    : isDark
                      ? "text-ink-gray-200 hover:bg-fill-dialog-border"
                      : "text-ink-ink hover:bg-fill-gray-100"
                }`}
              >
                {l}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const HOUR_OPTIONS   = HOURS.map((h) => ({ value: h, label: formatHour(h) }));
const MINUTE_OPTIONS = MINUTES.map((m) => ({ value: m, label: m === 0 ? ":00" : `:${m}` }));
const DAY_SELECT_OPTIONS = DAY_OPTIONS.map(({ key, label }) => ({ value: key, label }));

// ─── Edit-mode helper: decode an existing cron_expression into picker state ──
function parseCronState(cron) {
  if (!cron || ["EVERY_15MIN", "EVERY_30MIN", "HOURLY"].includes(cron)) {
    return { mode: "interval", intervalKey: cron || "EVERY_15MIN", dailyHour: 9, dailyMinute: 0, weeklyDay: "MON", weeklyHour: 9, weeklyMinute: 0 };
  }
  const daily = cron.match(/^DAILY_(\d+)(?:_(\d+))?$/);
  if (daily) {
    return { mode: "daily", intervalKey: "EVERY_15MIN", dailyHour: parseInt(daily[1], 10), dailyMinute: daily[2] ? parseInt(daily[2], 10) : 0, weeklyDay: "MON", weeklyHour: 9, weeklyMinute: 0 };
  }
  const weekly = cron.match(/^WEEKLY_([A-Z]+)_(\d+)(?:_(\d+))?$/);
  if (weekly) {
    return { mode: "weekly", intervalKey: "EVERY_15MIN", dailyHour: 9, dailyMinute: 0, weeklyDay: weekly[1], weeklyHour: parseInt(weekly[2], 10), weeklyMinute: weekly[3] ? parseInt(weekly[3], 10) : 0 };
  }
  return { mode: "interval", intervalKey: "EVERY_15MIN", dailyHour: 9, dailyMinute: 0, weeklyDay: "MON", weeklyHour: 9, weeklyMinute: 0 };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * SchedulerCard
 *
 * Inline card rendered inside the message row (same pattern as edit mode).
 * Pre-fills prompt and lets the user choose a schedule before saving.
 */
const SchedulerCard = ({
  theme, chat_id, space_id = null, message_id = null, source_prompt_id = null,
  source_answer_id = null, turn_id = null, prompt = "", onClose, job = null,
}) => {
  const isEditMode = !!job;
  const initState  = parseCronState(job?.cron_expression);

  const textareaRef = useRef(null);
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const [schedulePrompt, setSchedulePrompt] = useState(
    isEditMode ? (job.generation_input?.prompt || job.prompt || "") : prompt
  );
  const [mode, setMode]               = useState(initState.mode);
  const [intervalKey, setIntervalKey] = useState(initState.intervalKey);
  const [dailyHour, setDailyHour]     = useState(initState.dailyHour);
  const [dailyMinute, setDailyMinute] = useState(initState.dailyMinute);
  const [weeklyDay, setWeeklyDay]     = useState(initState.weeklyDay);
  const [weeklyHour, setWeeklyHour]   = useState(initState.weeklyHour);
  const [weeklyMinute, setWeeklyMinute] = useState(initState.weeklyMinute);
  const [label, setLabel]   = useState(isEditMode ? (job.label || "") : "");
  const [success, setSuccess] = useState(false);

  const cronExpression =
    mode === "interval" ? intervalKey
    : mode === "daily"  ? `DAILY_${dailyHour}_${dailyMinute}`
    :                     `WEEKLY_${weeklyDay}_${weeklyHour}_${weeklyMinute}`;

  const { mutate: createJob, isPending: isCreating, error: createError } = useCreateSchedulerJob({
    onSuccess: () => { setSuccess(true); setTimeout(onClose, 1400); },
  });
  const { mutate: updateJob, isPending: isUpdating, error: updateError } = useUpdateSchedulerJob({
    onSuccess: () => { setSuccess(true); setTimeout(onClose, 1400); },
  });
  const isPending = isCreating || isUpdating;
  const error     = createError || updateError;

  const handleSave = () => {
    if (!schedulePrompt.trim() || isPending) return;
    if (isEditMode) {
      updateJob({
        job_id: job.job_id,
        prompt: schedulePrompt.trim(),
        cron_expression: cronExpression,
        timezone: userTz,
        label: label.trim() || undefined,
      });
    } else {
      if (!chat_id) return;
      createJob({
        spark_id: chat_id,
        space_id: space_id || undefined,
        prompt: schedulePrompt.trim(),
        cron_expression: cronExpression,
        timezone: userTz,
        label: label.trim() || undefined,
        message_id: message_id || undefined,
        source_prompt_id: source_prompt_id || undefined,
        source_answer_id: source_answer_id || undefined,
        turn_id: turn_id || undefined,
      });
    }
  };

  const isDark = theme === "dark";

  // Shared chip style helpers
  const chipBase =
    "flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 cursor-pointer select-none";
  const chipActive =
    "photon-primary-action bg-button-primary border-transparent text-button-on-primary shadow-sm";
  const chipInactive = isDark
    ? "bg-transparent border-edge-muted-control text-ink-icon-hover hover:border-edge-selection-muted"
    : "bg-fill-white border-edge-border text-ink-selection-hover hover:border-edge-icon-hover";

  return (
    <div className={`w-full rounded-2xl ${isDark ? "bg-fill-hex-313235" : "bg-fill-hex-fafbfe"} border border-edge-border dark:border-edge-muted-control`}>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 pt-3.5 pb-2">
        <CalendarClock className="w-4 h-4 text-ink-primary dark:text-ink-accent shrink-0" />
        <span className="text-xs font-semibold text-ink-ink dark:text-ink-white tracking-wide">
          {isEditMode ? "Edit Schedule" : "Schedule Prompt"}
        </span>
      </div>

      {/* Prompt textarea */}
      <textarea
        autoFocus
        ref={textareaRef}
        value={schedulePrompt}
        rows={3}
        onChange={(e) => setSchedulePrompt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
        className={`text-[13px] py-2 px-4 w-full outline-none resize-none bg-transparent leading-6 ${
          isDark ? "text-ink-white placeholder-ink-selection-hover" : "text-ink-ink placeholder-ink-icon-hover"
        }`}
        placeholder="Prompt that will run on schedule…"
      />

      {/* Divider */}
      <div className="mx-4 border-t border-edge-border dark:border-edge-hex-3c3f45" />

      {/* Schedule controls */}
      <div className="px-4 py-3 space-y-3">

        {/* Mode tabs */}
        <div className="flex items-center gap-1.5">
          {MODE_TABS.map(({ id, label: tabLabel, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setMode(id)}
              className={`${chipBase} ${mode === id ? chipActive : chipInactive}`}
            >
              <Icon className="w-3 h-3" />
              {tabLabel}
            </button>
          ))}
        </div>

        {/* Interval chips */}
        {mode === "interval" && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-xs ${isDark ? "text-ink-icon-hover" : "text-ink-selection-hover"} mr-1`}>
              Every
            </span>
            {INTERVAL_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                onClick={() => setIntervalKey(opt.key)}
                title={opt.title}
                className={`${chipBase} ${intervalKey === opt.key ? chipActive : chipInactive}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Daily options */}
        {mode === "daily" && (
          <div className="flex items-center gap-2">
            <span className={`text-xs shrink-0 ${isDark ? "text-ink-icon-hover" : "text-ink-selection-hover"}`}>At</span>
            <ScrollSelect options={HOUR_OPTIONS}   value={dailyHour}   onChange={setDailyHour}   isDark={isDark} />
            <ScrollSelect options={MINUTE_OPTIONS} value={dailyMinute} onChange={setDailyMinute} isDark={isDark} width="w-20" />
          </div>
        )}

        {/* Weekly options */}
        {mode === "weekly" && (
          <div className="flex items-center gap-2">
            <ScrollSelect options={DAY_SELECT_OPTIONS} value={weeklyDay}    onChange={setWeeklyDay}    isDark={isDark} width="w-32" />
            <span className={`text-xs shrink-0 ${isDark ? "text-ink-icon-hover" : "text-ink-selection-hover"}`}>at</span>
            <ScrollSelect options={HOUR_OPTIONS}       value={weeklyHour}   onChange={setWeeklyHour}   isDark={isDark} />
            <ScrollSelect options={MINUTE_OPTIONS}     value={weeklyMinute} onChange={setWeeklyMinute} isDark={isDark} width="w-20" />
          </div>
        )}

        {/* Label (optional) */}
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          maxLength={80}
          placeholder="Label (optional)  —  e.g. Daily market brief"
          className={`w-full text-xs rounded-xl border px-3 py-2 outline-none focus:ring-2 focus:ring-focus-primary/30 bg-transparent ${
            isDark
              ? "border-edge-muted-control text-ink-white placeholder-ink-selection-hover"
              : "border-edge-border text-ink-ink placeholder-ink-icon-hover"
          }`}
        />

        {/* Error */}
        {error && (
          <p className="text-xs text-ink-red-400">{error.message}</p>
        )}

      </div>

      {/* Footer actions */}
      <div className="flex items-center justify-between px-4 pb-3 pt-1">
        {/* Summary pill */}
        <div className={`flex items-center gap-1.5 text-[11px] font-medium ${isDark ? "text-ink-icon-hover" : "text-ink-selection-hover"}`}>
          <CalendarClock className="w-3 h-3" />
          <ScheduleSummary mode={mode} intervalKey={intervalKey} dailyHour={dailyHour} dailyMinute={dailyMinute} weeklyDay={weeklyDay} weeklyHour={weeklyHour} weeklyMinute={weeklyMinute} />
        </div>

        <div className="flex items-center gap-2">
          {/* Cancel */}
          <button
            className={`flex items-center justify-center rounded-full w-7 h-7 border ${
              isDark
                ? "bg-fill-icon-hover bg-opacity-25 border-edge-muted-control"
                : "bg-fill-white border-edge-border"
            }`}
            onClick={onClose}
            disabled={isPending}
          >
            {isDark ? <WhiteCross className="w-3 h-3" /> : <BlackCross className="w-3 h-3" />}
          </button>

          {/* Schedule button */}
          <button
            disabled={isPending || !schedulePrompt.trim() || success}
            onClick={handleSave}
            className="photon-primary-action flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-button-primary text-button-on-primary text-xs font-semibold disabled:opacity-60 transition-colors"
          >
            {success ? (
              <>
                <Check className="w-3 h-3" />
                Saved
              </>
            ) : isPending ? (
              "Saving…"
            ) : (
              <>
                <CalendarClock className="w-3 h-3" />
                Schedule
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// Small helper: human-readable one-liner for the chosen option
function ScheduleSummary({ mode, intervalKey, dailyHour, dailyMinute, weeklyDay, weeklyHour, weeklyMinute }) {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  if (mode === "interval") {
    const map = {
      EVERY_15MIN: "Every 15 minutes",
      EVERY_30MIN: "Every 30 minutes",
      HOURLY:      "Every hour",
    };
    return <span>{map[intervalKey] || ""}</span>;
  }
  const DAY_NAMES = {
    MON: "Monday", TUE: "Tuesday", WED: "Wednesday",
    THU: "Thursday", FRI: "Friday", SAT: "Saturday", SUN: "Sunday",
  };
  if (mode === "daily") {
    return <span>Daily at {formatTime(dailyHour, dailyMinute)} · {tz}</span>;
  }
  return <span>Every {DAY_NAMES[weeklyDay]} at {formatTime(weeklyHour, weeklyMinute)} · {tz}</span>;
}

export default SchedulerCard;
